package org.teddapp.project.ui.backup

import org.teddapp.project.ui.TeddTokens

import androidx.compose.runtime.ReadOnlyComposable

import androidx.compose.runtime.Composable

import androidx.compose.ui.graphics.Color
import com.tedd.db.TeddDatabase
import org.teddapp.project.sync.EntityKind
import org.teddapp.project.sync.SyncEntityOps
import org.teddapp.project.sync.SyncRunEntry

/**
 * TEDD — Section 09 · Backup & Sync.
 *
 * Toutes les infos affichées sont calculées en direct depuis :
 *  - `TeddDatabase` (modules / domaines = rows réels par table)
 *  - `SyncManager.state` (historique, lastSyncAt, pending, status)
 *  - `AppSettings` (politique persistée)
 *
 * Plus aucun seed. Si rien n'a été synchronisé, on affiche un état "vide" honnête
 * plutôt que des entrées de démo.
 */

// ──────────────────────────────────────────────────────────────────
// Domaines = découpage UX des 36 entités sync en 10 modules
// ──────────────────────────────────────────────────────────────────

internal data class BackupDomain(
    val id: String,
    val name: String,
    val icon: String,
    val entities: List<EntityKind>,
) {
    /** Repère du module auquel le domaine appartient. Il ne teinte que son icône. */
    val color: Color
        @Composable @ReadOnlyComposable
        get() = when (id) {
            "d_obj", "d_proj" -> TeddTokens.ModGoals
            "d_focus" -> TeddTokens.ModFocus
            "d_finance" -> TeddTokens.ModFinance
            "d_portfolio" -> TeddTokens.ModPortfolio
            "d_notes" -> TeddTokens.ModNotes
            else -> TeddTokens.InkMuted
        }
}

internal val BACKUP_DOMAINS: List<BackupDomain> = listOf(
    BackupDomain("d_profile", "Profil & préférences", "users", listOf(
        EntityKind.UserProfile,
    )),
    BackupDomain("d_obj", "Objectifs", "target", listOf(
        EntityKind.Category, EntityKind.Objective, EntityKind.Milestone,
    )),
    BackupDomain("d_proj", "Projets", "grid", listOf(
        EntityKind.Organization, EntityKind.Project, EntityKind.ProjectStatus, EntityKind.Task,
    )),
    BackupDomain("d_focus", "Focus & Pomodoro", "focus", listOf(
        EntityKind.FocusSession, EntityKind.PomodoroSession,
        EntityKind.PomodoroItem, EntityKind.PomodoroCheck,
    )),
    BackupDomain("d_finance", "Finance", "wallet", listOf(
        EntityKind.FinanceAccount, EntityKind.FinanceCategory,
        EntityKind.FinanceTransaction, EntityKind.ExpenseList, EntityKind.ExpenseListItem,
        EntityKind.ExpenseEntry, EntityKind.Simulation, EntityKind.SimulationLine,
        EntityKind.Loan, EntityKind.LoanPayment,
    )),
    BackupDomain("d_portfolio", "Portfolio & compétences", "cards", listOf(
        EntityKind.CompetenceFamily, EntityKind.Competence, EntityKind.CompetenceCriterion,
        EntityKind.PortfolioPiece, EntityKind.PortfolioPieceCompetence,
    )),
    BackupDomain("d_notes", "Notes & cartes heuristiques", "notes", listOf(
        EntityKind.Notebook, EntityKind.Note,
        EntityKind.MindMap, EntityKind.MindMapNode, EntityKind.MindMapNodeNote,
    )),
)

/** Heuristique : ~0.2 Ko / ligne pour estimer le volume sans matérialiser le JSON. */
private const val KB_PER_ROW = 0.2

internal data class BackupDomainStats(
    val domain: BackupDomain,
    val rowCount: Int,
    val sizeKb: Int,
)

internal fun computeDomainStats(db: TeddDatabase): List<BackupDomainStats> =
    BACKUP_DOMAINS.map { d ->
        val total = d.entities.sumOf { SyncEntityOps.countAll(it, db) }
        BackupDomainStats(domain = d, rowCount = total, sizeKb = (total * KB_PER_ROW).toInt())
    }

/** Adapter vers `BackupModule` pour les composants UI déjà câblés. */
internal fun List<BackupDomainStats>.toUiModules(): List<BackupModule> = map { s ->
    BackupModule(
        id = s.domain.id,
        name = s.domain.name,
        icon = s.domain.icon,
        enabled = s.rowCount > 0,
        sizeKb = s.sizeKb,
        rowCount = s.rowCount,
    )
}

/** Nombre de syncs réussies dans le mois courant (UTC, basé sur nowMillis). */
internal fun successesThisMonth(history: List<SyncRunEntry>, now: Long): Int {
    val days = (now / 86_400_000L).toInt()
    val (y, mo, _) = org.teddapp.project.db.civilFromDays(days)
    val firstOfMonthDays = computeDaysOfFirstOfMonth(y, mo)
    val monthStartMs = firstOfMonthDays * 86_400_000L
    // Un passage partiel a envoyé des données : il compte.
    return history.count { (it.success || it.pushedCount > 0) && it.startedAt >= monthStartMs }
}

private fun computeDaysOfFirstOfMonth(y: Int, mo: Int): Long {
    val ya = if (mo <= 2) y - 1 else y
    val era = (if (ya >= 0) ya else ya - 399) / 400
    val yoe = (ya - era * 400).toLong()
    val mp = if (mo > 2) mo - 3 else mo + 9
    val doy = (153L * mp + 2) / 5
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    return era.toLong() * 146097L + doe - 719468L
}

// ──────────────────────────────────────────────────────────────────
// Tabs / enums UI
// ──────────────────────────────────────────────────────────────────

internal enum class BackupTab(val key: String, val label: String, val icon: String) {
    Overview("overview", "Vue d'ensemble", "home"),
    History("history",   "Historique",     "clock"),
    Tables("tables",     "Tables",         "list"),
    Export("export",     "Exporter",       "arrow-down"),
    Modules("modules",   "Modules inclus", "category"),
    Supabase("supabase", "Supabase",       "settings"),
}

internal enum class BackupStatus(val key: String) {
    Success("success"), Partial("partial"), Failed("failed");
    companion object { fun fromKey(k: String): BackupStatus = entries.first { it.key == k } }
}

internal enum class BackupTrigger(val key: String, val label: String) {
    AutoCloud("auto-cloud", "Auto · cloud"),
    AutoLocal("auto-local", "Auto · local"),
    Manual("manual",        "Manuel");
    companion object { fun fromKey(k: String): BackupTrigger = entries.first { it.key == k } }
}

internal enum class BackupDestination(val key: String, val label: String) {
    Server("server",     "Serveur teddApp"),
    Supabase("supabase", "Supabase"),
    Local("local",       "Disque local"),
    Download("download", "Téléchargé");
    companion object { fun fromKey(k: String): BackupDestination = entries.first { it.key == k } }
}

internal enum class BackupFormat(val key: String) {
    Json("json"), Sql("sql");
    val label: String get() = key.uppercase()
    companion object { fun fromKey(k: String): BackupFormat = entries.first { it.key == k } }
}

internal enum class BackupFrequency(val key: String, val label: String) {
    Daily("daily",                "Quotidienne"),
    Weekly("weekly",              "Hebdomadaire"),
    TwiceMonthly("twice_monthly", "2 fois par mois"),
    Monthly("monthly",            "Mensuelle");
    companion object { fun fromKey(k: String): BackupFrequency = entries.first { it.key == k } }
}

internal enum class BackupGapLevel { Ok, Info, Warning, Danger }

internal data class BackupGap(val level: BackupGapLevel, val message: String)

internal data class BackupEntry(
    val id: String,
    val date: String,
    val trigger: BackupTrigger,
    val format: BackupFormat,
    val sizeKb: Int,
    val status: BackupStatus,
    val destination: BackupDestination,
    val durationSec: Double,
    val modulesCount: Int,
    val note: String? = null,
) {
    val dayIso: String get() = date.substring(0, 10)
    val hhmm: String get() = date.substring(11, 16)
}

internal data class BackupModule(
    val id: String,
    val name: String,
    val icon: String,
    val enabled: Boolean,
    val sizeKb: Int,
    val rowCount: Int = 0,
) {
    /** Repère du module, le même que celui de son domaine. */
    val color: Color
        @Composable @ReadOnlyComposable
        get() = BACKUP_DOMAINS.firstOrNull { it.id == id }?.color ?: TeddTokens.InkMuted
}

internal data class BackupSettings(
    val autoBackup: Boolean = true,
    val frequency: BackupFrequency = BackupFrequency.TwiceMonthly,
)

// ──────────────────────────────────────────────────────────────────
// Helpers temporels (sans seed)
// ──────────────────────────────────────────────────────────────────

/** Minutes UTC depuis l'epoch pour un ISO "YYYY-MM-DDTHH:MM[:SS]". */
internal fun isoToEpochMinutes(iso: String): Long {
    val y  = iso.substring(0, 4).toInt()
    val mo = iso.substring(5, 7).toInt()
    val d  = iso.substring(8, 10).toInt()
    val hh = if (iso.length >= 13) iso.substring(11, 13).toInt() else 0
    val mm = if (iso.length >= 16) iso.substring(14, 16).toInt() else 0
    val ya = if (mo <= 2) y - 1 else y
    val era = (if (ya >= 0) ya else ya - 399) / 400
    val yoe = (ya - era * 400).toLong()
    val mp = if (mo > 2) mo - 3 else mo + 9
    val doy = (153 * mp + 2) / 5 + d - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    val days = era.toLong() * 146097L + doe - 719468L
    return days * 24L * 60L + hh * 60L + mm
}

/** Intervalle de rappel (ms) pour la fréquence configurée — miroir de SyncManager. */
internal fun frequencyToIntervalMs(frequency: BackupFrequency): Long = when (frequency) {
    BackupFrequency.Daily        -> 24L * 3_600_000L
    BackupFrequency.Weekly       -> 7L * 24L * 3_600_000L
    BackupFrequency.TwiceMonthly -> 15L * 24L * 3_600_000L
    BackupFrequency.Monthly      -> 30L * 24L * 3_600_000L
}

/**
 * Gap réel : calculé depuis `lastSyncAt` du SyncManager.
 *
 * - `cloudAvailable = false` (pas de creds Supabase) : on n'a aucune notion
 *   de retard cloud, donc Ok (la responsabilité retombe sur l'export manuel).
 * - `lastSyncAt == null` (cloud configuré, jamais sync) : Info, on suggère
 *   un premier cycle.
 * - sinon, comparaison à now() :
 *   * > 48 h → Warning
 *   * > 24 h → Info
 *   * sinon → Ok
 */
internal fun computeRealGap(cloudAvailable: Boolean, lastSyncAt: Long?, now: Long): BackupGap {
    if (!cloudAvailable) return BackupGap(BackupGapLevel.Ok, "Mode local uniquement")
    if (lastSyncAt == null) return BackupGap(BackupGapLevel.Info, "Première sync à lancer")
    val hrs = (now - lastSyncAt) / 3_600_000.0
    return when {
        hrs > 48 -> BackupGap(BackupGapLevel.Warning, "Pas de sync depuis ${hrs.toInt()} h")
        hrs > 24 -> BackupGap(BackupGapLevel.Info, "Prévoir une sync de fin de journée")
        else     -> BackupGap(BackupGapLevel.Ok, "À jour")
    }
}

private val MONTHS_FR_SHORT = listOf("jan", "fév", "mar", "avr", "mai", "juin", "juil", "août", "sep", "oct", "nov", "déc")

/** "2026-05-22" → "22 mai 2026". */
internal fun fmtDate(iso: String): String {
    val y = iso.substring(0, 4)
    val m = iso.substring(5, 7).toInt() - 1
    val d = iso.substring(8, 10).trimStart('0').ifEmpty { "0" }
    return "$d ${MONTHS_FR_SHORT.getOrElse(m) { "?" }} $y"
}

/** Étiquette relative pour un jour, par rapport à "today" passé en argument. */
internal fun fmtDayLabel(iso: String, today: String): String {
    val deltaDays = (isoToEpochMinutes("${today}T00:00") - isoToEpochMinutes("${iso}T00:00")) / 1440L
    return when (deltaDays) {
        0L -> "Aujourd'hui"
        1L -> "Hier"
        2L -> "Avant-hier"
        else -> fmtDate(iso)
    }
}

/** Variante qui dérive `today` automatiquement depuis nowMillis(). */
internal fun fmtDayLabel(iso: String): String {
    val now = org.teddapp.project.db.nowMillis()
    val days = (now / 86_400_000L).toInt()
    val (y, mo, d) = org.teddapp.project.db.civilFromDays(days)
    val today = buildString {
        append(y.toString().padStart(4, '0')); append('-')
        append(mo.toString().padStart(2, '0')); append('-')
        append(d.toString().padStart(2, '0'))
    }
    return fmtDayLabel(iso, today)
}

/**
 * Étiquette "Prochaine sauvegarde" honnête :
 *  - sync cloud désactivée / auto désactivée → "Manuelle"
 *  - pas encore de lastSyncAt → "Au prochain cycle"
 *  - sinon, formate la durée restante jusqu'à `lastSyncAt + intervalMs`
 */
internal fun nextScheduledLabel(
    cloudAvailable: Boolean,
    cloudEnabled: Boolean,
    autoEnabled: Boolean,
    frequency: BackupFrequency,
    lastSyncAt: Long?,
    now: Long,
): String {
    if (!cloudAvailable) return "Relie d'abord Supabase"
    if (!cloudEnabled)   return "Synchro coupée"
    if (!autoEnabled)    return "Manuelle uniquement"
    val interval = frequencyToIntervalMs(frequency)
    val nextAt = (lastSyncAt ?: now) + interval
    val deltaMs = nextAt - now
    if (deltaMs <= 0) return "Maintenant"
    val mins = deltaMs / 60_000L
    val hours = mins / 60L
    val days = hours / 24L
    return when {
        days >= 2  -> "Dans $days jours"
        days == 1L -> "Dans 1 jour"
        hours >= 2 -> "Dans $hours h"
        hours == 1L -> "Dans 1 h"
        else       -> "Dans $mins min"
    }
}

/** Epoch ms → "AAAA-MM-JJTHH:MM:SS" (UTC), format attendu par les entrées d'historique. */
internal fun epochToIsoMinutes(epochMs: Long): String {
    val totalSeconds = epochMs / 1000L
    val days = (totalSeconds / 86_400L).toInt()
    val rem = (totalSeconds % 86_400L).toInt().let { if (it < 0) it + 86_400 else it }
    val hh = rem / 3600
    val mm = (rem % 3600) / 60
    val ss = rem % 60
    val (y, mo, d) = org.teddapp.project.db.civilFromDays(days)
    return buildString {
        append(y.toString().padStart(4, '0')); append('-')
        append(mo.toString().padStart(2, '0')); append('-')
        append(d.toString().padStart(2, '0')); append('T')
        append(hh.toString().padStart(2, '0')); append(':')
        append(mm.toString().padStart(2, '0')); append(':')
        append(ss.toString().padStart(2, '0'))
    }
}
